import { PatientEntityType } from "@/lib/supabase/supabaseTypes";

/**
 * Calculate Levenshtein distance between two strings
 * Returns the minimum number of single-character edits needed to transform one string into another
 */
export function levenshteinDistance(str1: string, str2: string): number {
	const s1 = str1.toLowerCase();
	const s2 = str2.toLowerCase();

	if (s1.length === 0) return s2.length;
	if (s2.length === 0) return s1.length;

	const matrix: number[][] = [];

	for (let i = 0; i <= s1.length; i++) {
		matrix[i] = [i];
	}
	for (let j = 0; j <= s2.length; j++) {
		matrix[0][j] = j;
	}

	for (let i = 1; i <= s1.length; i++) {
		for (let j = 1; j <= s2.length; j++) {
			const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
			matrix[i][j] = Math.min(
				matrix[i - 1][j] + 1,
				matrix[i][j - 1] + 1,
				matrix[i - 1][j - 1] + cost
			);
		}
	}

	return matrix[s1.length][s2.length];
}

/**
 * Calculate similarity ratio between two strings (0 to 1)
 * 1 = identical, 0 = completely different
 */
export function stringSimilarity(str1: string, str2: string): number {
	if (!str1 && !str2) return 1;
	if (!str1 || !str2) return 0;

	const maxLength = Math.max(str1.length, str2.length);
	if (maxLength === 0) return 1;

	const distance = levenshteinDistance(str1, str2);
	return 1 - distance / maxLength;
}

const FIRST_NAME_WEIGHT = 0.4;
const LAST_NAME_WEIGHT = 0.6;

/**
 * A phonetic (sounds-alike) match is a strong signal even when the spelling is far
 * apart, so it floors the per-part similarity that drives the displayed confidence.
 */
const PHONETIC_SIMILARITY_FLOOR = 0.85;

export type MatchStrictness = "strict" | "medium" | "loose";

export interface DuplicateMatchOptions {
	/** Largest per-name edit distance (after folding) that still counts as a match. */
	maxPartDistance: number;
	/**
	 * Also treat phonetically identical parts as a match via Cologne phonetics
	 * (Meier=Meyer=Maier=Mayer, Schmidt=Schmitt). This is the knob that makes the
	 * looser levels usable for people with dyslexia / a spelling difficulty, who
	 * tend to write a name the way it sounds rather than the way it is spelled.
	 */
	usePhonetic: boolean;
	/** Fold umlauts/accents/ß so "Müller" = "Mueller" = "Muller"; off only on Streng. */
	foldUmlauts: boolean;
	/**
	 * Minimum edit similarity (0–1) a part must keep for a distance-based match.
	 * A flat edit budget alone would let short unrelated names pass on Locker
	 * ("Anna" vs "Hans" is 3 edits) — requiring at least half the name preserved
	 * keeps "some similarity" an actual similarity. Phonetic equality bypasses this.
	 */
	minPartSimilarity: number;
	/**
	 * Require both name parts present and matching, and reject the candidate if either
	 * present part conflicts. True on every slider level — both names must show some
	 * similarity. Only the birth-date corroboration path in evaluatePatientMatch
	 * relaxes this, letting a single matching part suffice when the birth dates are
	 * identical.
	 */
	requireBothParts: boolean;
}

/**
 * The three tolerance levels behind the patient-selector slider, strictest first.
 * Streng = the literally identical name (only invisible differences — case,
 * whitespace, separators, unicode encoding — are ignored). Mittel folds
 * umlauts/accents and forgives up to two edits per name or a phonetically identical
 * spelling. Locker stretches the budget to three edits per name for heavier typos,
 * but only while at least half the name stays intact. Every level requires some
 * similarity in BOTH first and last name: a present part that conflicts rejects the
 * candidate (keeps families apart), and a missing part never counts as a match.
 */
export const MATCH_LEVELS: Record<MatchStrictness, DuplicateMatchOptions> = {
	strict: {
		maxPartDistance: 0,
		usePhonetic: false,
		foldUmlauts: false,
		requireBothParts: true,
		minPartSimilarity: 0,
	},
	medium: {
		maxPartDistance: 2,
		usePhonetic: true,
		foldUmlauts: true,
		requireBothParts: true,
		minPartSimilarity: 0,
	},
	loose: {
		maxPartDistance: 3,
		usePhonetic: true,
		foldUmlauts: true,
		requireBothParts: true,
		minPartSimilarity: 0.5,
	},
};

/** Slider positions, strictest → loosest; the slider value is an index into this. */
export const STRICTNESS_ORDER: MatchStrictness[] = [
	"strict",
	"medium",
	"loose",
];

const COMBINING_MARKS = /[\u0300-\u036f]/g;

/**
 * Cologne phonetics (Kölner Phonetik) — the German-tuned equivalent of Soundex.
 * Maps a word to a digit code built from its consonant skeleton so that names
 * which SOUND the same collapse together: Meier/Meyer/Maier/Mayer → "67",
 * Schmidt/Schmitt → "862", Fischer/Fisher → "387". Returns "" for input without
 * codeable letters. See https://de.wikipedia.org/wiki/Kölner_Phonetik.
 */
export function colognePhonetic(value: string): string {
	const s = value
		.toUpperCase()
		.replace(/Ä/g, "A")
		.replace(/Ö/g, "O")
		.replace(/Ü/g, "U")
		.replace(/ß/g, "SS")
		.replace(/Ø/g, "O")
		.replace(/Å/g, "A")
		.replace(/Æ/g, "AE")
		.replace(/Œ/g, "OE")
		.normalize("NFD")
		.replace(COMBINING_MARKS, "")
		.replace(/[^A-Z]/g, "");
	if (!s) return "";

	const VOWELS = "AEIJOUY";
	let codes = "";
	for (let i = 0; i < s.length; i++) {
		const c = s[i];
		const prev = s[i - 1];
		// Sentinel (not a letter) for word end: "".includes() is always true in JS,
		// which would mis-code a word-final D/T/C (e.g. "Schmidt").
		const next = s[i + 1] ?? " ";
		if (VOWELS.includes(c)) {
			codes += "0";
		} else if (c === "H") {
			// H carries no code of its own
		} else if (c === "B") {
			codes += "1";
		} else if (c === "P") {
			codes += next === "H" ? "3" : "1";
		} else if (c === "D" || c === "T") {
			codes += "CSZ".includes(next) ? "8" : "2";
		} else if (c === "F" || c === "V" || c === "W") {
			codes += "3";
		} else if (c === "G" || c === "K" || c === "Q") {
			codes += "4";
		} else if (c === "C") {
			if (i === 0) {
				codes += "AHKLOQRUX".includes(next) ? "4" : "8";
			} else if (prev === "S" || prev === "Z") {
				codes += "8";
			} else {
				codes += "AHKOQUX".includes(next) ? "4" : "8";
			}
		} else if (c === "X") {
			codes += prev === "C" || prev === "K" || prev === "Q" ? "8" : "48";
		} else if (c === "L") {
			codes += "5";
		} else if (c === "M" || c === "N") {
			codes += "6";
		} else if (c === "R") {
			codes += "7";
		} else if (c === "S" || c === "Z") {
			codes += "8";
		}
	}

	let collapsed = "";
	for (const d of codes) {
		if (d !== collapsed[collapsed.length - 1]) collapsed += d;
	}
	const leadingZero = collapsed.startsWith("0") ? "0" : "";
	return leadingZero + collapsed.slice(leadingZero ? 1 : 0).replace(/0/g, "");
}

/**
 * Normalize a name for edit-distance comparison. Always drops the differences a
 * human can't see or wouldn't count as part of the name — case, surrounding and
 * inner whitespace, separators/punctuation, zero-width and NFC-vs-NFD encoding.
 * With `foldUmlauts` (every level except Streng) accents are additionally stripped
 * and ä/ö/ü/ß expanded, so "Müller", "Mueller" and "müller" compare equal —
 * dropping or rewriting an umlaut is not a typo. Streng keeps accents/umlauts
 * distinct, so it means the literally written name.
 */
function normalizeName(value: string, foldUmlauts: boolean): string {
	const base = value.normalize("NFC").toLowerCase();
	if (!foldUmlauts) {
		return base.replace(/[^\p{L}\p{N}]/gu, "");
	}
	return base
		.replace(/ä/g, "ae")
		.replace(/ö/g, "oe")
		.replace(/ü/g, "ue")
		.replace(/ß/g, "ss")
		.replace(/ø/g, "o")
		.replace(/å/g, "a")
		.replace(/æ/g, "ae")
		.replace(/œ/g, "oe")
		.normalize("NFD")
		.replace(COMBINING_MARKS, "")
		.replace(/[^a-z0-9]/g, "");
}

type NamePartVerdict = "match" | "conflict" | "unknown";

/**
 * Compare one name part (e.g. both first names). A part is `unknown` when either
 * side is empty — we genuinely can't tell — and only `match`/`conflict` when both
 * sides are present. It matches when the folded names are within `maxPartDistance`
 * edits OR (with `usePhonetic`) sound identical. Distinguishing "missing" from
 * "different" is the whole point: a missing part must not disqualify a candidate,
 * but a genuinely different one must, or family members sharing a surname flood the
 * results.
 */
function compareNamePart(
	a: string | null,
	b: string | null,
	options: DuplicateMatchOptions
): { verdict: NamePartVerdict; similarity: number } {
	const left = a?.trim();
	const right = b?.trim();
	if (!left || !right) {
		return { verdict: "unknown", similarity: 0 };
	}
	const na = normalizeName(left, options.foldUmlauts);
	const nb = normalizeName(right, options.foldUmlauts);
	if (!na || !nb) {
		return { verdict: "unknown", similarity: 0 };
	}

	const distance = levenshteinDistance(na, nb);
	const maxLength = Math.max(na.length, nb.length);
	const editSimilarity = maxLength === 0 ? 1 : 1 - distance / maxLength;

	const phoneticLeft = options.usePhonetic ? colognePhonetic(left) : "";
	const phoneticEqual =
		phoneticLeft !== "" && phoneticLeft === colognePhonetic(right);

	// Cap the budget for very short names: a typo must leave at least one character
	// in common, never be a full replacement. Without this, distance 2 lets two
	// unrelated 2-letter names ("Ed" vs "Ng") fall within the loose budget.
	const allowedDistance = Math.min(options.maxPartDistance, maxLength - 1);
	const isMatch =
		(distance <= allowedDistance &&
			editSimilarity >= options.minPartSimilarity) ||
		phoneticEqual;
	const similarity = phoneticEqual
		? Math.max(editSimilarity, PHONETIC_SIMILARITY_FLOOR)
		: editSimilarity;

	return { verdict: isMatch ? "match" : "conflict", similarity };
}

export interface PatientDuplicateEvaluation {
	isDuplicate: boolean;
	/** Confidence over the name parts that could actually be compared (0–1). */
	score: number;
}

/**
 * Decide whether two patients are likely the same person, by name only.
 *
 * With `requireBothParts` (every slider level) both parts must be present and match,
 * and a single *conflicting* part (both present but dissimilar) rejects the candidate
 * — this keeps families apart ("Hans Müller" vs "Anna Müller" → first-name conflict).
 * Without it (only the birth-date corroboration path) a single matching part is
 * enough and the other part no longer vetoes whether it is missing or different.
 *
 * `score` is the weighted similarity over the parts that were actually comparable
 * (last name weighted higher), renormalised so a missing part neither inflates
 * nor deflates it. It is the single source of truth for both the filter and the
 * displayed confidence badge.
 */
export function evaluatePatientDuplicate(
	sourceFirstName: string | null,
	sourceLastName: string | null,
	targetFirstName: string | null,
	targetLastName: string | null,
	options: DuplicateMatchOptions
): PatientDuplicateEvaluation {
	const first = compareNamePart(sourceFirstName, targetFirstName, options);
	const last = compareNamePart(sourceLastName, targetLastName, options);

	// A conflicting part only vetoes when both parts are required (all slider levels).
	// On the birth-date corroboration path a strong single part wins, so a different
	// other part drags the score down rather than rejecting the candidate.
	const hasConflict =
		first.verdict === "conflict" || last.verdict === "conflict";
	if (hasConflict && options.requireBothParts) {
		return { isDuplicate: false, score: 0 };
	}

	const matchCount =
		(first.verdict === "match" ? 1 : 0) +
		(last.verdict === "match" ? 1 : 0);
	if (matchCount < (options.requireBothParts ? 2 : 1)) {
		return { isDuplicate: false, score: 0 };
	}

	let weightedSum = 0;
	let weightTotal = 0;
	if (first.verdict !== "unknown") {
		weightedSum += FIRST_NAME_WEIGHT * first.similarity;
		weightTotal += FIRST_NAME_WEIGHT;
	}
	if (last.verdict !== "unknown") {
		weightedSum += LAST_NAME_WEIGHT * last.similarity;
		weightTotal += LAST_NAME_WEIGHT;
	}

	return {
		isDuplicate: true,
		score: weightTotal > 0 ? weightedSum / weightTotal : 0,
	};
}

/**
 * Why a candidate surfaced. The name matcher is blind to two strong identity
 * signals that a human relies on: a deliberately re-used patient code and an
 * identical date of birth. Either marks the same person even when the names were
 * entered differently — e.g. the same patient recorded once privately and once
 * under a dentist, or with a typo the name matcher would reject as a conflict.
 */
export type PatientMatchReason = "code" | "birthdate" | "name";

/** An identical date of birth is strong corroboration; it floors the displayed score. */
const BIRTHDATE_SIMILARITY_FLOOR = 0.9;

function normalizeCode(code: string | null | undefined): string {
	return (code ?? "").trim().toLowerCase();
}

/**
 * Both codes present (non-empty) and equal AND not an auto-generated XX-N code.
 * Auto codes are an org-wide running counter, so two unrelated patients can share
 * one (e.g. after a delete renumbers the sequence) — treating those as a conclusive
 * duplicate would propose strangers as the same person. Only a deliberately entered,
 * shared code is a real identity signal.
 */
function sameCode(
	a: string | null | undefined,
	b: string | null | undefined
): boolean {
	const normalized = normalizeCode(a);
	if (normalized === "" || normalized !== normalizeCode(b)) return false;
	return !isAutoGeneratedPatientCode(a);
}

/** Both birth dates present and equal (date columns compare as plain ISO strings). */
function sameBirthDate(
	a: string | null | undefined,
	b: string | null | undefined
): boolean {
	const normalized = (a ?? "").trim();
	return normalized !== "" && normalized === (b ?? "").trim();
}

export interface PatientMatchEvaluation extends PatientDuplicateEvaluation {
	reason: PatientMatchReason | null;
}

/**
 * Full duplicate decision layered on top of the name-only matcher. An identical
 * patient code is conclusive on its own (it bypasses the name gate entirely). An
 * identical date of birth lets a single matching name part stand in for the usual
 * both-parts requirement and floors the score, so the same person entered with
 * differing names still surfaces. With neither identity signal present this is
 * exactly the name matcher, so existing behaviour is unchanged.
 */
export function evaluatePatientMatch(
	source: PatientEntityType,
	candidate: PatientEntityType,
	options: DuplicateMatchOptions
): PatientMatchEvaluation {
	if (sameCode(source.code, candidate.code)) {
		return { isDuplicate: true, score: 1, reason: "code" };
	}

	const byName = evaluatePatientDuplicate(
		source.first_name,
		source.last_name,
		candidate.first_name,
		candidate.last_name,
		options
	);
	const birthDateEqual = sameBirthDate(
		source.birth_date,
		candidate.birth_date
	);

	if (byName.isDuplicate) {
		if (birthDateEqual) {
			return {
				isDuplicate: true,
				score: Math.max(byName.score, BIRTHDATE_SIMILARITY_FLOOR),
				reason: "birthdate",
			};
		}
		return { ...byName, reason: "name" };
	}

	// The name gate rejected the candidate, but an identical birth date plus any
	// single matching name part is enough to propose it for human confirmation.
	if (birthDateEqual) {
		const bySinglePart = evaluatePatientDuplicate(
			source.first_name,
			source.last_name,
			candidate.first_name,
			candidate.last_name,
			{ ...options, requireBothParts: false }
		);
		if (bySinglePart.isDuplicate) {
			return {
				isDuplicate: true,
				score: Math.max(bySinglePart.score, BIRTHDATE_SIMILARITY_FLOOR),
				reason: "birthdate",
			};
		}
	}

	return { isDuplicate: false, score: 0, reason: null };
}

/**
 * Rank candidate patients by how likely they are duplicates of `source`, using
 * evaluatePatientMatch (name + code + birth date) as the single source of truth
 * for both the keep/drop decision and the score, and surfacing why each matched.
 *
 * Deliberately does no prefix/character anchoring and applies no row cap: the
 * caller must pass the full candidate set. A prefix filter would pre-exclude the
 * exact duplicates this is meant to catch (leading-character typos, umlaut
 * variants like "Müller" vs "Mueller", transpositions), and an unordered cap can
 * drop the best match before scoring.
 */
export function rankPotentialDuplicates(
	source: PatientEntityType,
	candidates: PatientEntityType[],
	options: DuplicateMatchOptions
): Array<
	PatientEntityType & {
		similarityScore: number;
		matchReason: PatientMatchReason;
	}
> {
	const matches: Array<
		PatientEntityType & {
			similarityScore: number;
			matchReason: PatientMatchReason;
		}
	> = [];
	for (const candidate of candidates) {
		const { isDuplicate, score, reason } = evaluatePatientMatch(
			source,
			candidate,
			options
		);
		if (isDuplicate && reason) {
			matches.push({
				...candidate,
				similarityScore: score,
				matchReason: reason,
			});
		}
	}
	return matches.sort((a, b) => b.similarityScore - a.similarityScore);
}

/**
 * Detect auto-generated patient codes produced by the `set_patient_code` trigger
 * (supabase/triggers/set_patient_code.sql). Format: `{first_initial}{last_initial}-{count}`
 * where each initial is either an UPPERCASE letter (from the first char of the name)
 * or the literal '0' placeholder when the name is empty/null.
 * Examples: `JD-42`, `A0-105`, `0D-7`, `00-5`.
 */
const AUTO_GENERATED_PATIENT_CODE_REGEX = /^[A-Z0]{2}-\d+$/;

export function isAutoGeneratedPatientCode(
	code: string | null | undefined
): boolean {
	if (!code) return false;
	return AUTO_GENERATED_PATIENT_CODE_REGEX.test(code.trim());
}
